import api from "./api";

// ── Feature gate ────────────────────────────────────────────────────────────
export const getVoiceStatus = async () => {
  const { data } = await api.get("/voice/status");
  return data;
};

// ── Connections (admin) ─────────────────────────────────────────────────────
export const getVoiceProviders = async () => {
  const { data } = await api.get("/voice/providers");
  return data;
};
export const listVoiceConnections = async () => {
  const { data } = await api.get("/voice/connections");
  return data;
};
export const createVoiceConnection = async (payload) => {
  const { data } = await api.post("/voice/connections", payload);
  return data;
};
export const updateVoiceConnection = async (id, payload) => {
  const { data } = await api.put(`/voice/connections/${id}`, payload);
  return data;
};
export const deleteVoiceConnection = async (id) => {
  const { data } = await api.delete(`/voice/connections/${id}`);
  return data;
};
export const testVoiceConnection = async (id) => {
  const { data } = await api.post(`/voice/connections/${id}/test`);
  return data;
};
export const provisionVoiceConnection = async (id) => {
  const { data } = await api.post(`/voice/connections/${id}/provision`);
  return data;
};
export const testVoiceCall = async (id, toNumber) => {
  const { data } = await api.post(`/voice/connections/${id}/test-call`, { toNumber });
  return data;
};

// ── Settings (admin) ────────────────────────────────────────────────────────
export const getVoiceSettings = async () => {
  const { data } = await api.get("/voice/settings");
  return data;
};
export const updateVoiceSettings = async (payload) => {
  const { data } = await api.put("/voice/settings", payload);
  return data;
};

// ── Bridge Calling: agent's own callback number ─────────────────────────────
export const getMyVoicePhone = async () => {
  const { data } = await api.get("/voice/my-phone");
  return data;
};
export const setMyVoicePhone = async (phoneNumber) => {
  const { data } = await api.put("/voice/my-phone", { phoneNumber });
  return data;
};
export const requestPhoneVerification = async () => {
  const { data } = await api.post("/voice/my-phone/verify/request");
  return data;
};
export const confirmPhoneVerification = async (code) => {
  const { data } = await api.post("/voice/my-phone/verify/confirm", { code });
  return data;
};

// ── Softphone (browser WebRTC agent leg) ────────────────────────────────────
// Mints a short-lived Twilio Voice AccessToken. Identity is derived server-side
// from req.user — never passed from the client.
export const getSoftphoneToken = async () => {
  const { data } = await api.post("/voice/softphone/token");
  return data;
};

// ── Calls (agent) ───────────────────────────────────────────────────────────
// `mode` ("softphone" | "bridge") selects the agent media leg; `fallbackOf`
// carries the failed softphone CallSession id so the server bypasses the
// per-(company,user,number) dedupe lock on an auto-fallback re-dial.
export const startCall = async ({ toNumber, contactId, ticketId, purpose, mode, fallbackOf } = {}) => {
  const { data } = await api.post("/voice/calls", {
    toNumber,
    contactId,
    ticketId,
    purpose,
    mode,
    fallbackOf,
  });
  return data;
};
export const getCall = async (id) => {
  const { data } = await api.get(`/voice/calls/${id}`);
  return data;
};
export const endCall = async (id) => {
  const { data } = await api.post(`/voice/calls/${id}/end`);
  return data;
};
export const setCallOutcome = async (id, outcome) => {
  const { data } = await api.put(`/voice/calls/${id}/outcome`, { outcome });
  return data;
};
export const setCallNotes = async (id, notes) => {
  const { data } = await api.put(`/voice/calls/${id}/notes`, { notes });
  return data;
};
export const listCallsByContact = async (contactId) => {
  const { data } = await api.get(`/voice/contacts/${contactId}/calls`);
  return data;
};
export const getCallTranscript = async (id) => {
  const { data } = await api.get(`/voice/calls/${id}/transcript`);
  return data;
};
export const analyzeCall = async (id) => {
  const { data } = await api.post(`/voice/calls/${id}/analyze`);
  return data;
};
// On-demand: transcribe + (unless analyze:false) AI-analyze. Returns 202.
export const processCall = async (id, analyze = true) => {
  const { data } = await api.post(`/voice/calls/${id}/process`, { analyze });
  return data;
};
export const recordingUrl = (recordingId, download = false) =>
  `/voice/recordings/${recordingId}${download ? "?download=1" : ""}`;

// ── Customer Memory ─────────────────────────────────────────────────────────
export const listMemory = async (contactId, status) => {
  const { data } = await api.get(`/voice/contacts/${contactId}/memory`, {
    params: status ? { status } : {},
  });
  return data;
};
export const createMemory = async (contactId, payload) => {
  const { data } = await api.post(`/voice/contacts/${contactId}/memory`, payload);
  return data;
};
export const confirmMemory = async (id, ticketId) => {
  const { data } = await api.post(`/voice/memory/${id}/confirm`, { ticketId });
  return data;
};
export const updateMemory = async (id, payload) => {
  const { data } = await api.put(`/voice/memory/${id}`, payload);
  return data;
};
export const dismissMemory = async (id) => {
  const { data } = await api.post(`/voice/memory/${id}/dismiss`);
  return data;
};
export const deleteMemory = async (id) => {
  const { data } = await api.delete(`/voice/memory/${id}`);
  return data;
};

// ── Collection ──────────────────────────────────────────────────────────────
export const listCollections = async (contactId) => {
  const { data } = await api.get(`/voice/contacts/${contactId}/collections`);
  return data;
};
export const createCollection = async (payload) => {
  const { data } = await api.post("/voice/collections", payload);
  return data;
};
export const updateCollection = async (id, payload) => {
  const { data } = await api.put(`/voice/collections/${id}`, payload);
  return data;
};
export const getCollectionScript = async (payload) => {
  const { data } = await api.post("/voice/collection/script", payload);
  return data;
};

// ── Timeline ────────────────────────────────────────────────────────────────
export const getCommunicationTimeline = async (contactId) => {
  const { data } = await api.get(`/voice/contacts/${contactId}/timeline`);
  return data;
};
